import { Box, Typography, useTheme, MenuItem, TextField, Button, Autocomplete, Breadcrumbs } from "@mui/material";
import InventoryIcon    from "@mui/icons-material/Inventory";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FilterListIcon   from "@mui/icons-material/FilterList";

import { useProductLogList } from "api/hooks/inventory";
import { useSelector }       from "react-redux";
import { useState }          from "react";
import moment                from "moment";
import { requester }         from "api/pesabackend";
import { useLocation, useNavigate } from "react-router-dom";

import ReportTable, { RRow, RCell } from "components/Tables/ReportTable";

const ACTION_COLORS = {
  PURCHASE:   { bg: "rgba(29,158,117,0.1)",   color: "#1D9E75" },
  SALE:       { bg: "rgba(16,142,212,0.1)",   color: "#108ED4" },
  ADJUSTMENT: { bg: "rgba(186,117,23,0.1)",   color: "#BA7517" },
  RETURN:     { bg: "rgba(226,75,74,0.1)",    color: "#e24b4a" },
};

const ActionPill = ({ action }) => {
  const c = ACTION_COLORS[action] ?? { bg: "rgba(136,135,128,0.1)", color: "#888780" };
  return (
    <Box sx={{
      display: "inline-flex", alignItems: "center", gap: "5px",
      px: "8px", py: "3px", borderRadius: "20px",
      backgroundColor: c.bg,
    }}>
      <Box sx={{ width: "5px", height: "5px", borderRadius: "50%", backgroundColor: c.color, flexShrink: 0 }} />
      <Typography sx={{ fontSize: "11px", fontWeight: 600, color: c.color, lineHeight: 1 }}>
        {action}
      </Typography>
    </Box>
  );
};

const PERIOD_PRESETS = ["TODAY", "THIS WEEK", "THIS MONTH", "CUSTOM"];
const ACTION_OPTIONS = ["all", "PURCHASE", "SALE", "ADJUSTMENT", "RETURN"];

const inputSx = (cardBg, border) => ({
  "& .MuiInputBase-root": { fontSize: "13px", height: "36px", backgroundColor: cardBg, borderRadius: "8px" },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: border },
  "& .MuiInputLabel-root": { fontSize: "12px" },
});

export default function ProductLog() {
  const location = useLocation();
  const navigate = useNavigate();
  const theme    = useTheme();
  const isDark   = theme.palette.mode === "dark";

  const bodyBg  = theme.palette.background.alt;
  const cardBg  = isDark ? "rgba(255,255,255,0.03)" : "#ffffff";
  const accent  = theme.palette.secondary.main;
  const textPri = theme.palette.neutral.dark;
  const textSec = theme.palette.neutral.main;
  const border  = isDark ? "rgba(29,158,117,0.14)" : "rgba(29,158,117,0.16)";

  const business_branch_id = useSelector((s) => s.authReducer.business_branch_id);

  const [showCustom,       setShowCustom]       = useState(false);
  const [startDate,        setStartDate]        = useState(moment().startOf("month").format("YYYY-MM-DD"));
  const [endDate,          setEndDate]          = useState(moment().endOf("month").format("YYYY-MM-DD"));
  const [activePeriod,     setActivePeriod]     = useState("THIS MONTH");
  const [product,          setProduct]          = useState(location.state.productList.length > 0 ? location.state.productList[0].id : "");
  const [productList,      setProductList]      = useState(location.state.productList);
  const { productLogList, isLoadingProductLogList, setAction, setFilter } =
    useProductLogList(1, business_branch_id, product);

  const fetchItemList = async (search) => {
    try {
      if (search && search.length >= 3) {
        const res = await requester.get(
          `/inventory/products/list?business_branch=${business_branch_id}&search=${search}&typeFilter=all`
        );
        setProductList(res.data.data);
      }
    } catch {
    }
  };

  const handlePeriod = (val) => {
    setActivePeriod(val);
    if (val === "TODAY")      { setStartDate(moment().format("YYYY-MM-DD")); setEndDate(moment().format("YYYY-MM-DD")); setShowCustom(false); }
    if (val === "THIS WEEK")  { setStartDate(moment().startOf("week").format("YYYY-MM-DD")); setEndDate(moment().endOf("week").format("YYYY-MM-DD")); setShowCustom(false); }
    if (val === "THIS MONTH") { setStartDate(moment().startOf("month").format("YYYY-MM-DD")); setEndDate(moment().endOf("month").format("YYYY-MM-DD")); setShowCustom(false); }
    if (val === "CUSTOM")     { setShowCustom(true); }
  };

  const columns = [
    { label: "Date" },
    { label: "Item Name" },
    { label: "Description" },
    { label: "Action" },
    { label: "Transaction No." },
    { label: "Quantity", align: "center" },
    { label: "Batch No." },
    { label: "Cost",     align: "right" },
    { label: "Done By" },
  ];

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: bodyBg, p: "1.5rem 2rem", display: "flex", flexDirection: "column", gap: "1.5rem" }}>

      {/* ── Header ── */}
      <Box>
        <Breadcrumbs
          separator={<ChevronRightIcon sx={{ fontSize: "14px", color: border }} />}
          sx={{ mb: "0.5rem" }}
        >
          <Typography
            onClick={() => navigate("/inventory")}
            sx={{ fontSize: "12px", color: accent, cursor: "pointer", display: "flex", alignItems: "center", gap: "4px", "&:hover": { textDecoration: "underline" } }}
          >
            <InventoryIcon sx={{ fontSize: "14px" }} /> Inventory
          </Typography>
          <Typography sx={{ fontSize: "12px", color: textSec }}>Item Logs</Typography>
        </Breadcrumbs>

        <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Box sx={{
            width: "40px", height: "40px", borderRadius: "10px",
            backgroundColor: "rgba(29,158,117,0.12)",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: accent, flexShrink: 0,
          }}>
            <InventoryIcon sx={{ fontSize: "22px" }} />
          </Box>
          <Box>
            <Typography sx={{ fontFamily: "'DM Serif Display', serif", fontSize: "22px", fontWeight: 400, color: textPri, lineHeight: 1.2 }}>
              Item Logs
            </Typography>
            <Typography sx={{ fontSize: "12px", color: textSec, mt: "2px" }}>
              {productLogList.length} log entries
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* ── Filter bar ── */}
      <Box sx={{
        backgroundColor: cardBg,
        border: `1px solid ${border}`,
        borderRadius: "10px",
        p: "0.875rem 1rem",
        display: "flex", flexWrap: "wrap", gap: "0.75rem", alignItems: "center",
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px", color: accent, flexShrink: 0 }}>
          <FilterListIcon sx={{ fontSize: "16px" }} />
          <Typography sx={{ fontSize: "11px", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.07em" }}>
            Filters
          </Typography>
        </Box>

        {/* Product search */}
        <Autocomplete
          size="small"
          onOpen={() => fetchItemList(null)}
          defaultValue={productList[0] ?? null}
          onChange={(_, value) => {
            if (value) setProduct(value.id);
          }}
          options={productList}
          getOptionLabel={(o) => `${o.product_name} ${o.description}`}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          renderOption={(props, o) => (
            <Box component="li" {...props} key={o.id}>
              {o.product_name} {o.description}
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select product"
              onChange={(e) => fetchItemList(e.currentTarget.value)}
              sx={inputSx(cardBg, border)}
            />
          )}
          sx={{ width: "240px" }}
        />

        {/* Action filter */}
        <TextField label="Action" size="small" select
          defaultValue="all"
          onChange={(e) => setAction(e.target.value)}
          sx={{ ...inputSx(cardBg, border), width: "140px" }}>
          {ACTION_OPTIONS.map((a) => (
            <MenuItem key={a} value={a}>
              {a === "all" ? "All actions" : a}
            </MenuItem>
          ))}
        </TextField>

        {/* Divider */}
        <Box sx={{ width: "1px", height: "24px", backgroundColor: border, mx: "0.25rem", flexShrink: 0 }} />

        {/* Period pills */}
        <Box sx={{ display: "flex", gap: "5px", flexWrap: "wrap" }}>
          {PERIOD_PRESETS.filter((p) => p !== "CUSTOM").map((p) => (
            <Box key={p} onClick={() => handlePeriod(p)} sx={{
              px: "0.65rem", py: "0.25rem", borderRadius: "20px",
              fontSize: "12px", fontWeight: 500, cursor: "pointer",
              border: `1px solid ${activePeriod === p ? accent : border}`,
              color: activePeriod === p ? accent : textSec,
              backgroundColor: activePeriod === p ? "rgba(29,158,117,0.1)" : "transparent",
              transition: "all 0.15s",
              "&:hover": { borderColor: accent, color: accent },
            }}>
              {p === "THIS WEEK" ? "This week" : p === "THIS MONTH" ? "This month" : "Today"}
            </Box>
          ))}
          <Box onClick={() => handlePeriod("CUSTOM")} sx={{
            px: "0.65rem", py: "0.25rem", borderRadius: "20px",
            fontSize: "12px", fontWeight: 500, cursor: "pointer",
            border: `1px solid ${showCustom ? accent : border}`,
            color: showCustom ? accent : textSec,
            backgroundColor: showCustom ? "rgba(29,158,117,0.1)" : "transparent",
            transition: "all 0.15s",
            "&:hover": { borderColor: accent, color: accent },
          }}>
            Custom range
          </Box>
        </Box>

        {/* Custom date inputs */}
        {showCustom && (
          <>
            <TextField label="Start date" type="date" size="small"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              inputProps={{ max: endDate }}
              sx={{ ...inputSx(cardBg, border), width: "150px" }} />
            <TextField label="End date" type="date" size="small"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              inputProps={{ min: startDate }}
              sx={{ ...inputSx(cardBg, border), width: "150px" }} />
          </>
        )}

        <Button
          onClick={() => setFilter({ start: startDate, end: endDate })}
          sx={{
            fontSize: "12px", fontWeight: 500,
            px: "1rem", py: "0.35rem",
            borderRadius: "8px",
            backgroundColor: accent,
            color: "#fff", textTransform: "none",
            "&:hover": { backgroundColor: "#0F6E56" },
            flexShrink: 0,
          }}
        >
          Apply
        </Button>
      </Box>

      {/* ── Table ── */}
      <ReportTable
        columns={columns}
        rows={productLogList}
        loading={isLoadingProductLogList}
        emptyMessage="No log entries found for the selected filters"
        renderRow={(row, index) => (
          <RRow key={`${row.id ?? index}-${index}`} index={index}>
            <RCell>{moment(row.date_added).format("LLL")}</RCell>
            <RCell>{row.related_product.product_name}</RCell>
            <RCell>{row.related_product.description}</RCell>
            <RCell><ActionPill action={row.action} /></RCell>
            <RCell>{row.transaction_no ?? "—"}</RCell>
            <RCell align="center">{row.quantity}</RCell>
            <RCell>{row.related_batch}</RCell>
            <RCell align="right">{row.cost}</RCell>
            <RCell>{row.added_by.first_name} {row.added_by.last_name}</RCell>
          </RRow>
        )}
      />
    </Box>
  );
}